import { Link } from 'react-router-dom'
import { CONTACT, ENTITY } from '../constants'
import { LEGAL_EMAIL, PRIVACY_EMAIL } from '../legal/meta'
import { LegalLayout, LegalSection } from '../legal/LegalLayout'

export function Terms() {
  return (
    <LegalLayout
      eyebrow="Legal sheet"
      title="Terms of use"
      summary="These Terms govern access to passwork.ae, our custom software services, and the Compound subscription. By using the site or purchasing a subscription, you agree to this sheet."
      documentTitle="Terms of Use | Passwork Software Solution"
      description="Terms of Use for Passwork FZ LLC — passwork.ae, custom software services, and Compound subscription."
    >
      <LegalSection code="T-01" title="Who we are">
        <p>
          The site and services are operated by {ENTITY} (“Passwork”, “we”,
          “us”). Contact: {CONTACT}. These Terms form a binding agreement
          between you (an individual or the organization you represent) and us.
        </p>
      </LegalSection>

      <LegalSection code="T-02" title="What these Terms cover">
        <p>
          They apply to: (a) browsing and contacting us via passwork.ae; (b)
          statements of work and engagement models for custom software
          development; and (c) the Compound product — living skills, support,
          training, and related enablement sold on a subscription basis.
        </p>
        <p>
          Separate written contracts (NDAs, statements of work, order forms)
          prevail over these Terms if they conflict on the same subject.
        </p>
      </LegalSection>

      <LegalSection code="T-03" title="Eligibility and accounts">
        <p>
          Compound and professional services are offered to organizations and
          to individuals acting in a business capacity. You confirm you have
          authority to bind your organization. You must provide accurate
          contact and billing details and keep them current.
        </p>
      </LegalSection>

      <LegalSection code="T-04" title="Compound subscription">
        <p>
          A Compound subscription grants a non-exclusive, non-transferable
          license for your organization to use the skill materials, method
          packs, and related documentation we provide, solely for your internal
          product development, for the paid term.
        </p>
        <p>
          Subscription fees are as stated on the Compound page or your order
          form (currently published as USD 2,000 per month or USD 20,000 per
          year, per organization). Fees are due in advance unless we agree
          otherwise in writing. Taxes may apply.
        </p>
        <p>
          Access may include living skill updates, priority support, staff
          training, index onboarding, office hours, and stack alignment as
          described on the product sheet. We may improve or reorganize
          materials; we will not materially reduce the core subscription
          without notice for the remainder of a prepaid annual term.
        </p>
      </LegalSection>

      <LegalSection code="T-05" title="Acceptable use">
        <p>You must not:</p>
        <ul className="list-none space-y-2">
          <li className="flex gap-3">
            <span className="mt-2.5 h-px w-3 shrink-0 bg-overprint" aria-hidden="true" />
            Resell, sublicense, or publicly redistribute Compound materials
            outside your organization without our written consent
          </li>
          <li className="flex gap-3">
            <span className="mt-2.5 h-px w-3 shrink-0 bg-overprint" aria-hidden="true" />
            Remove proprietary notices or present our method as your own
            product offering
          </li>
          <li className="flex gap-3">
            <span className="mt-2.5 h-px w-3 shrink-0 bg-overprint" aria-hidden="true" />
            Use the site or services to violate law, infringe rights, or probe
            systems you are not authorized to test
          </li>
          <li className="flex gap-3">
            <span className="mt-2.5 h-px w-3 shrink-0 bg-overprint" aria-hidden="true" />
            Attempt to bypass access controls or abuse support channels
          </li>
        </ul>
      </LegalSection>

      <LegalSection code="T-06" title="Client materials and AI tooling">
        <p>
          You retain ownership of your code, data, and confidential materials.
          You grant us a limited license to use them only as needed to deliver
          the subscribed services or an agreed engagement. Compound runs in your
          environments (including third-party IDEs such as Cursor); you are
          responsible for your accounts with those providers and for reviewing
          outputs before production use.
        </p>
        <p>
          AI accelerates volume. You remain accountable for architecture,
          merges, security, and compliance of anything you ship.
        </p>
      </LegalSection>

      <LegalSection code="T-07" title="Intellectual property">
        <p>
          Passwork retains all rights in the site, Compound skills, trademarks,
          and our pre-existing tools and know-how. Deliverables under a custom
          engagement transfer as set out in that engagement’s contract. Feedback
          you give about Compound may be used by us to improve the product
          without obligation to you.
        </p>
      </LegalSection>

      <LegalSection code="T-08" title="Confidentiality">
        <p>
          Each party must protect the other’s non-public information with
          reasonable care and use it only for the relationship. Obligations
          survive for three years after disclosure, or longer for trade secrets
          while they remain secret. Public information and independently
          developed materials are excluded.
        </p>
      </LegalSection>

      <LegalSection code="T-09" title="Disclaimers">
        <p>
          The site and Compound materials are provided “as is” to the fullest
          extent permitted by law. We do not warrant uninterrupted access,
          that AI-assisted outputs will be error-free, or that Compound will
          meet every unspoken requirement. Case studies and savings figures on
          the site are illustrative estimates, not guarantees.
        </p>
      </LegalSection>

      <LegalSection code="T-10" title="Liability">
        <p>
          To the maximum extent permitted by applicable law, neither party is
          liable for indirect, incidental, special, consequential, or lost-profit
          damages. Our aggregate liability arising from these Terms or Compound
          in any twelve-month period is limited to the fees you paid us for
          Compound in that period (or USD 1,000 if you only used the public
          site). Nothing limits liability for fraud, death or personal injury
          caused by negligence, or other liability that cannot be limited by law.
        </p>
      </LegalSection>

      <LegalSection code="T-11" title="Term, renewal, termination">
        <p>
          Monthly plans renew month to month until cancelled. Annual plans run
          for twelve months and renew unless either party gives notice before
          the renewal date as stated on the order form (default: 14 days). We
          may suspend or terminate for material breach, non-payment, or
          unlawful use. Upon end of term, you must stop using Compound
          materials except for copies retained solely as archival records
          required by law or your internal audit policy.
        </p>
      </LegalSection>

      <LegalSection code="T-12" title="Privacy">
        <p>
          Personal data is handled under our{' '}
          <Link to="/privacy" className="index-link text-ink">
            Privacy Policy
          </Link>{' '}
          and{' '}
          <Link to="/cookies" className="index-link text-ink">
            Cookie Notice
          </Link>
          . Those documents form part of your agreement with us for site use.
        </p>
      </LegalSection>

      <LegalSection code="T-13" title="Governing law">
        <p>
          These Terms are governed by the laws of the United Arab Emirates as
          applied in Ras al Khaimah, without regard to conflict-of-law rules.
          Courts of Ras al Khaimah have exclusive jurisdiction, except that we
          may seek injunctive relief in any forum for IP or confidentiality
          breaches.
        </p>
      </LegalSection>

      <LegalSection code="T-14" title="Changes">
        <p>
          We may update these Terms by posting a revised sheet with a new
          effective date. Material changes to Compound commercial terms will be
          notified to active subscribers at the email on file. Continued use
          after the effective date constitutes acceptance, except where local
          law requires explicit consent.
        </p>
        <p>
          Questions: {LEGAL_EMAIL} · privacy matters: {PRIVACY_EMAIL}.
        </p>
      </LegalSection>
    </LegalLayout>
  )
}
